const encoder = new TextEncoder();
const decoder = new TextDecoder();
const routers = new WeakMap();
function isPromiseLike(value) {
    return Boolean(value) && typeof value.then === "function";
}
function bytesFrom(value) {
    if (value == null)
        return new Uint8Array();
    if (value instanceof Uint8Array)
        return value;
    if (ArrayBuffer.isView(value)) {
        return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    }
    if (value instanceof ArrayBuffer)
        return new Uint8Array(value);
    if (typeof SharedArrayBuffer !== "undefined" && value instanceof SharedArrayBuffer) {
        return new Uint8Array(value);
    }
    if (typeof value === "string")
        return encoder.encode(value);
    return encoder.encode(JSON.stringify(value));
}
function serializableRequest(request) {
    return {
        identifier: request.identifier,
        uri: request.uri,
        method: request.method || "GET",
        metadata: request.metadata ?? {},
    };
}
/**
 * Renderer-level facade over LensCore's low-level URI listener API.
 *
 * A route handler receives decoded helpers (`text`, `json()`), response
 * helpers, and `retain(key)`. Retaining a request keeps its identifier so the
 * host can deliver any number of later messages through the same URI call.
 */
export class LensUriRouter {
    engine;
    historyLimit;
    _rawPromise;
    _routes;
    _subscriptions;
    _listeners;
    _history;
    constructor(engine, { historyLimit = 200 } = {}) {
        if (!engine?.ensureRaw) {
            throw new Error("LensCore engine does not expose ensureRaw().");
        }
        this.engine = engine;
        this.historyLimit = historyLimit;
        this._rawPromise = null;
        this._routes = new Map();
        this._subscriptions = new Map();
        this._listeners = new Set();
        this._history = [];
    }
    get history() {
        return this._history.map((entry) => ({ ...entry }));
    }
    onEvent(listener) {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }
    async _raw() {
        if (!this._rawPromise) {
            this._rawPromise = Promise.resolve(this.engine.ensureRaw()).then((raw) => {
                if (!raw?.registerUriListener || !raw?.provideUriResponse) {
                    throw new Error("LensCore runtime does not expose URI listener APIs.");
                }
                return raw;
            });
        }
        return this._rawPromise;
    }
    _emit(type, detail = {}) {
        const entry = { type, at: Date.now(), ...detail };
        this._history.push(entry);
        if (this._history.length > this.historyLimit) {
            this._history.splice(0, this._history.length - this.historyLimit);
        }
        for (const listener of this._listeners)
            listener(entry);
        return entry;
    }
    /** Register one LensCore URI scheme/prefix pair. */
    async register({ scheme, prefix, handleRequest, cancelRequest = () => undefined, }) {
        const routeKey = `${scheme}://${prefix}`;
        if (this._routes.has(routeKey)) {
            throw new Error(`URI route already registered: ${routeKey}`);
        }
        const raw = await this._raw();
        const route = { scheme, prefix, active: true };
        this._routes.set(routeKey, route);
        raw.registerUriListener(scheme, prefix, {
            handleRequest: (request) => {
                const normalizedRequest = request;
                if (!route.active)
                    return undefined;
                const context = this._requestContext(normalizedRequest, routeKey);
                this._emit("request", { route: routeKey, request: serializableRequest(normalizedRequest) });
                try {
                    const result = handleRequest(context);
                    if (isPromiseLike(result)) {
                        Promise.resolve(result).catch((error) => this._emit("error", {
                            route: routeKey,
                            request: serializableRequest(normalizedRequest),
                            message: error instanceof Error ? error.message : String(error),
                        }));
                    }
                    return result;
                }
                catch (error) {
                    this._emit("error", {
                        route: routeKey,
                        request: serializableRequest(normalizedRequest),
                        message: error instanceof Error ? error.message : String(error),
                    });
                    throw error;
                }
            },
            cancelRequest: (request) => {
                const normalizedRequest = request;
                for (const [key, subscription] of this._subscriptions) {
                    if (subscription.identifier === request.identifier) {
                        this._subscriptions.delete(key);
                        this._emit("subscription-cancelled", { key, request: serializableRequest(normalizedRequest) });
                    }
                }
                this._emit("cancelled", { route: routeKey, request: serializableRequest(normalizedRequest) });
                return cancelRequest(this._requestContext(normalizedRequest, routeKey));
            },
        });
        this._emit("listening", { route: routeKey, scheme, prefix });
        return {
            route: routeKey,
            dispose: () => {
                route.active = false;
                this._routes.delete(routeKey);
                this._emit("route-disabled", { route: routeKey });
            },
        };
    }
    _requestContext(request, route) {
        const data = bytesFrom(request.data);
        let text;
        return {
            ...request,
            data,
            route,
            get text() {
                if (text === undefined)
                    text = decoder.decode(data);
                return text;
            },
            json() {
                const body = this.text;
                return body ? JSON.parse(body) : null;
            },
            respond: (body, options) => this.respond(request.identifier, body, options),
            respondJson: (body, options) => this.respondJson(request.identifier, body, options),
            retain: (key, metadata) => this.retain(key, request, metadata),
        };
    }
    retain(key, request, metadata = {}) {
        const subscriptionKey = String(key || "").trim();
        if (!subscriptionKey)
            throw new Error("A retained URI subscription needs a key.");
        const subscription = {
            key: subscriptionKey,
            ...serializableRequest(request),
            metadata,
            retainedAt: Date.now(),
        };
        this._subscriptions.set(subscriptionKey, subscription);
        this._emit("subscription-retained", { subscription: { ...subscription } });
        return { ...subscription };
    }
    getSubscription(key) {
        const subscription = this._subscriptions.get(String(key));
        return subscription ? { ...subscription } : null;
    }
    listSubscriptions() {
        return Array.from(this._subscriptions.values(), (subscription) => ({ ...subscription }));
    }
    async respond(identifier, body, options = {}) {
        const raw = await this._raw();
        const response = {
            code: options.code ?? 200,
            description: options.description ?? "OK",
            contentType: options.contentType ?? (typeof body === "string" ? "text/plain" : "application/octet-stream"),
            metadata: options.metadata ?? {},
            data: bytesFrom(body),
        };
        raw.provideUriResponse(identifier, response);
        this._emit("response", {
            identifier,
            code: response.code,
            contentType: response.contentType,
            byteLength: response.data.byteLength,
        });
        return { identifier, code: response.code, contentType: response.contentType, byteLength: response.data.byteLength };
    }
    respondJson(identifier, value, options = {}) {
        return this.respond(identifier, JSON.stringify(value), {
            ...options,
            contentType: options.contentType ?? "application/json",
        });
    }
    async send(key, body, options = {}) {
        const subscription = this._subscriptions.get(String(key));
        if (!subscription)
            throw new Error(`No lens URI subscription named '${key}'.`);
        const result = await this.respond(subscription.identifier, body, options);
        this._emit("subscription-sent", { key: subscription.key, ...result });
        return { key: subscription.key, ...result };
    }
    sendJson(key, value, options = {}) {
        return this.send(key, JSON.stringify(value), {
            ...options,
            contentType: options.contentType ?? "application/json",
        });
    }
}
/** Return the single router associated with an engine instance. */
export function getLensUriRouter(engine, options) {
    if (!engine || (typeof engine !== "object" && typeof engine !== "function")) {
        throw new Error("A LensCore engine instance is required.");
    }
    const key = engine;
    let router = routers.get(key);
    if (!router) {
        router = new LensUriRouter(engine, options);
        routers.set(key, router);
    }
    return router;
}
export function decodeLensUriData(data) {
    const text = decoder.decode(bytesFrom(data));
    if (!text)
        return { text: "", json: null };
    try {
        return { text, json: JSON.parse(text) };
    }
    catch {
        return { text, json: null };
    }
}
//# sourceMappingURL=lensUriRouter.js.map